// Device identifier for anti-abuse (self-written, no third-party service).
// Hashes stable browser signals (canvas, WebGL, audio, font probe, screen, timezone, languages,
// platform, CPU/memory, touch) into a SHA-256 hex id. Only the hash and a coarse summary leave the
// browser; never raw canvas images. Computed lazily (idle time) and kept in localStorage.

const ID_KEY = 'yc_did';
const META_KEY = 'yc_dmeta';
const VERSION = 1;

let pending = null;

const safe = (fn, fallback = '') => {
    try {
        const v = fn();
        return v === undefined || v === null ? fallback : v;
    } catch (_) {
        return fallback;
    }
};

const ascii = (s, max = 80) => String(s || '').replace(/[^\x20-\x7e]/g, '').slice(0, max);

const readStore = (key) => safe(() => window.localStorage.getItem(key), null);
const writeStore = (key, value) => safe(() => { window.localStorage.setItem(key, value); return true; }, false);

// FNV-1a 32-bit, used only where SubtleCrypto is missing (non-secure context).
const fnv = (str) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
};

const sha256 = async (str) => {
    const subtle = safe(() => window.crypto && window.crypto.subtle, null);
    if (subtle && window.TextEncoder) {
        const buf = await subtle.digest('SHA-256', new TextEncoder().encode(str));
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    // 8 rounds of salted FNV → 64 hex chars
    let out = '';
    for (let i = 0; i < 8; i++) out += fnv(i + ':' + str);
    return out;
};

const canvasSignal = () => safe(() => {
    const c = document.createElement('canvas');
    c.width = 240;
    c.height = 60;
    const ctx = c.getContext('2d');
    if (!ctx) return '';
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(100, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Yeap Cloud, \u{1F600} 0123', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Yeap Cloud, \u{1F600} 0123', 4, 17);
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = 'rgb(255,0,255)';
    ctx.beginPath();
    ctx.arc(50, 30, 20, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
    return fnv(c.toDataURL()) + fnv(c.toDataURL().slice(-200));
});

const webglSignal = () => safe(() => {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
    if (!gl) return { vendor: '', renderer: '', params: '' };
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    const params = [
        gl.MAX_TEXTURE_SIZE, gl.MAX_RENDERBUFFER_SIZE, gl.MAX_VERTEX_ATTRIBS, gl.MAX_VARYING_VECTORS,
        gl.MAX_VERTEX_UNIFORM_VECTORS, gl.MAX_FRAGMENT_UNIFORM_VECTORS, gl.MAX_TEXTURE_IMAGE_UNITS,
    ].map((p) => safe(() => String(gl.getParameter(p)))).join(',');
    const ext = safe(() => (gl.getSupportedExtensions() || []).length, 0);
    return { vendor: String(vendor || ''), renderer: String(renderer || ''), params: params + '|' + ext };
}, { vendor: '', renderer: '', params: '' });

const audioSignal = () => new Promise((resolve) => {
    const Ctx = safe(() => window.OfflineAudioContext || window.webkitOfflineAudioContext, null);
    if (!Ctx) {
        resolve('');
        return;
    }
    let done = false;
    const finish = (v) => {
        if (!done) {
            done = true;
            resolve(v);
        }
    };
    setTimeout(() => finish('timeout'), 600);
    try {
        const ctx = new Ctx(1, 5000, 44100);
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = 10000;
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -50;
        comp.knee.value = 40;
        comp.ratio.value = 12;
        comp.attack.value = 0;
        comp.release.value = 0.25;
        osc.connect(comp);
        comp.connect(ctx.destination);
        osc.start(0);
        ctx.oncomplete = (e) => {
            const data = e.renderedBuffer.getChannelData(0);
            let sum = 0;
            for (let i = 4500; i < 5000; i++) sum += Math.abs(data[i]);
            finish(sum.toFixed(6));
        };
        const p = ctx.startRendering();
        if (p && p.catch) p.catch(() => finish(''));
    } catch (_) {
        finish('');
    }
});

const FONT_PROBE = [
    'Arial', 'Arial Black', 'Calibri', 'Cambria', 'Comic Sans MS', 'Consolas', 'Courier New', 'Georgia',
    'Helvetica', 'Helvetica Neue', 'Impact', 'Lucida Console', 'Menlo', 'Monaco', 'Palatino', 'Segoe UI',
    'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Roboto', 'Noto Sans', 'Ubuntu', 'DejaVu Sans',
    'PingFang SC', 'Microsoft YaHei', 'SimSun', 'Hiragino Sans', 'Meiryo', 'Malgun Gothic', 'Apple Color Emoji',
    'Segoe UI Emoji', 'Source Han Sans', 'Liberation Sans', 'Cantarell', 'Franklin Gothic', 'Gill Sans',
];

const fontSignal = () => safe(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return [];
    const text = 'mmmmmmmmmlli10OQ@#';
    const base = {};
    ['monospace', 'serif', 'sans-serif'].forEach((b) => {
        ctx.font = `72px ${b}`;
        base[b] = ctx.measureText(text).width;
    });
    return FONT_PROBE.filter((f) => ['monospace', 'serif', 'sans-serif'].some((b) => {
        ctx.font = `72px '${f}', ${b}`;
        return ctx.measureText(text).width !== base[b];
    }));
}, []);

const collect = async () => {
    const nav = window.navigator || {};
    const scr = window.screen || {};
    const gl = webglSignal();
    const fonts = fontSignal();
    const audio = await audioSignal();
    const canvas = canvasSignal();
    const tz = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone, '');
    const langs = safe(() => (nav.languages && nav.languages.length ? nav.languages : [nav.language]).join(','), '');
    const screenStr = `${Math.max(scr.width || 0, scr.height || 0)}x${Math.min(scr.width || 0, scr.height || 0)}x${scr.colorDepth || 0}`;
    const signals = {
        v: VERSION,
        canvas,
        glv: gl.vendor,
        glr: gl.renderer,
        glp: gl.params,
        audio,
        fonts: fonts.join(','),
        screen: screenStr,
        dpr: safe(() => String(window.devicePixelRatio || 1), ''),
        tz,
        tzo: safe(() => String(new Date(2026, 0, 1).getTimezoneOffset()), ''),
        langs,
        platform: safe(() => nav.platform, ''),
        hc: safe(() => String(nav.hardwareConcurrency || ''), ''),
        dm: safe(() => String(nav.deviceMemory || ''), ''),
        touch: safe(() => String(nav.maxTouchPoints || 0), ''),
        vendor: safe(() => nav.vendor, ''),
    };
    const id = await sha256(JSON.stringify(signals));
    const meta = {
        v: VERSION,
        tz: ascii(tz, 40),
        sc: screenStr,
        dpr: signals.dpr,
        hc: signals.hc,
        dm: signals.dm,
        tp: signals.touch,
        pl: ascii(signals.platform, 30),
        lang: ascii(langs, 40),
        gl: ascii(gl.renderer, 80),
        fonts: fonts.length,
        cv: canvas.slice(0, 8),
        au: ascii(audio, 12),
    };
    return { id, meta };
};

/** Cached id (sync, may be null before the first computation finishes). */
export const getDeviceIdSync = () => {
    const id = readStore(ID_KEY);
    return id && /^[a-f0-9]{64}$/.test(id) ? id : null;
};

export const getDeviceMetaSync = () => readStore(META_KEY);

/** Compute once per page (or reuse localStorage). Resolves to the id, or null on failure. */
export const ensureDeviceId = () => {
    const cached = getDeviceIdSync();
    if (cached) return Promise.resolve(cached);
    if (!pending) {
        pending = collect()
            .then(({ id, meta }) => {
                writeStore(ID_KEY, id);
                writeStore(META_KEY, JSON.stringify(meta));
                return id;
            })
            .catch(() => null);
    }
    return pending;
};

/** Start computing in idle time so the first paint isn't delayed. */
export const warmDeviceId = () => {
    if (getDeviceIdSync() || pending || typeof window === 'undefined') return;
    const run = () => { ensureDeviceId(); };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1200);
};
